/*
 * Copyright (c) 2021 - 2026 All rights reserved by Ferenc Farkas <wolferi93@gmail.com>
 */

/**
 * Resolves the Happy widget palette for the current appearance. Android widgets
 * render from a headless JS task with no React theme context, so this reads the
 * system color scheme (honoring the user's persisted light/dark/system choice
 * when available) and returns the same tokens the app uses (`app/config/themes.js`).
 * Dark on a dark phone, light on a light phone — exactly as required.
 */

import { Appearance } from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';

import { lightTheme, darkTheme } from '../config/themes';

/** AsyncStorage key the app persists the theme mode under (ThemeContext). */
const THEME_MODE_KEY = 'userThemeMode';

/**
 * Resolve `{ isDark, colors }` for widget rendering. Prefers the user's explicit
 * app choice, falling back to the OS appearance for 'system'/unset.
 * @returns {Promise<{ isDark: boolean, colors: object }>}
 */
export const resolveWidgetTheme = async () => {
    let mode = 'system';
    try {
        mode = (await AsyncStorage.getItem(THEME_MODE_KEY)) || 'system';
    } catch (_error) {
        mode = 'system';
    }

    let isDark;
    if (mode === 'dark') {
        isDark = true;
    } else if (mode === 'light') {
        isDark = false;
    } else {
        isDark = Appearance.getColorScheme() === 'dark';
    }

    return { isDark, colors: (isDark ? darkTheme : lightTheme).colors };
};

/** Synchronous variant using only the OS appearance (no stored preference). */
export const resolveWidgetThemeSync = () => {
    const isDark = Appearance.getColorScheme() === 'dark';

    return { isDark, colors: (isDark ? darkTheme : lightTheme).colors };
};
